<template>
  <div class="min-h-full bg-slate-50 pb-12">
    <div class="max-w-3xl mx-auto px-4 py-6 space-y-4">

      <!-- 1. 标题区 -->
      <div class="bg-white rounded-lg shadow-sm p-6">
        <h1 class="text-2xl font-bold text-slate-900 leading-tight mb-4">
          {{ expertCase.title }}
        </h1>

        <div class="w-12 h-0.5 bg-amber-400 mb-4"></div>

        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-full bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center text-white font-bold text-sm overflow-hidden cursor-pointer hover:ring-2 hover:ring-amber-400 transition-all">
              <img v-if="expert && expert.avatar" :src="expert.avatar" :alt="expert.name" class="w-full h-full object-cover" />
              <template v-else>{{ (expert && expert.name && expert.name[0]) || '专' }}</template>
            </div>
            <div>
              <div class="font-medium text-slate-900">{{ (expert && expert.name) || '专家' }}</div>
              <div class="text-xs text-slate-500">{{ (expert && expert.title) || '' }}</div>
            </div>
          </div>

          <div class="flex items-center gap-4 text-xs text-slate-400">
            <span class="flex items-center gap-1">
              <IconEye class="w-3.5 h-3.5" />
              {{ (expertCase.views && expertCase.views.toLocaleString()) || '1,234' }}
            </span>
            <span class="flex items-center gap-1">
              <IconClock class="w-3.5 h-3.5" />
              {{ expertCase.duration || '15 分钟' }}
            </span>
          </div>
        </div>
      </div>

      <!-- 2. 视频区块 -->
      <div v-if="expertCase.videoUrl" ref="videoSectionRef" class="bg-white rounded-lg shadow-sm p-6">
        <div
          ref="videoContainerRef"
          class="aspect-video bg-black relative group rounded-lg overflow-hidden cursor-pointer"
          @click="handleVideoContainerClick"
        >
          <!-- 加载状态 -->
          <div v-if="isVideoLoading" class="absolute inset-0 bg-slate-900 flex items-center justify-center z-10">
            <div class="flex flex-col items-center gap-3">
              <IconLoader class="w-10 h-10 text-amber-400 animate-spin" />
              <span class="text-sm text-slate-400">视频加载中...</span>
            </div>
          </div>

          <video
            ref="videoRef"
            class="w-full h-full"
            :poster="`https://picsum.photos/seed/${expertCase.id}-video/1280/720`"
            @timeupdate="onVideoTimeUpdate"
            @loadedmetadata="onVideoLoadedMetadata"
            @ended="isPlayingVideo = false"
            @waiting="isVideoLoading = true"
            @playing="isVideoLoading = false"
          >
            <source :src="expertCase.videoUrl || 'https://www.w3schools.com/html/mov_bbb.mp4'" type="video/mp4" />
          </video>

          <!-- Custom Controls Overlay -->
          <div class="video-controls absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-4">
            <div class="bg-black/40 backdrop-blur-md rounded-xl p-3 flex items-center justify-between text-white">
              <div class="flex items-center gap-3">
                <button
                  @click.stop="toggleVideo"
                  class="hover:text-amber-400 transition-colors"
                >
                  <IconPause v-if="isPlayingVideo" class="w-5 h-5 fill-current" />
                  <IconPlay v-else class="w-5 h-5 fill-current" />
                </button>
                <div class="text-xs font-mono">
                  {{ formatTime(videoCurrentTime) }} / {{ formatTime(videoDuration) }}
                </div>
              </div>
              <div class="flex items-center gap-3">
                <button
                  @click.stop="handleVideoSpeed"
                  class="text-xs font-bold px-2 py-0.5 border border-white/30 rounded hover:bg-white/20 transition-all"
                >
                  {{ videoSpeed }}x
                </button>
                <button
                  @click.stop="toggleFullscreen"
                  class="hover:text-amber-400 transition-colors"
                >
                  <IconMinimize2 v-if="isFullscreen" class="w-4 h-4" />
                  <IconMaximize2 v-else class="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          <!-- 播放按钮 -->
          <button
            v-if="!isPlayingVideo && !isVideoLoading"
            @click.stop="toggleVideo"
            class="absolute inset-0 flex items-center justify-center group-hover:scale-110 transition-transform"
          >
            <div class="w-14 h-14 bg-amber-400 rounded-full flex items-center justify-center shadow-xl">
              <IconPlay class="w-7 h-7 text-white fill-current ml-0.5" />
            </div>
          </button>
        </div>
      </div>

      <!-- 3. 音频+正文合并区块 -->
      <div ref="contentSectionRef" class="bg-white rounded-lg shadow-sm p-6">
        <!-- 音频播放器 -->
        <template v-if="expertCase.audioUrl">
          <audio
            ref="audioRef"
            @timeupdate="onAudioTimeUpdate"
            @loadedmetadata="onAudioLoadedMetadata"
            @ended="isPlayingAudio = false"
            @waiting="isAudioLoading = true"
            @playing="isAudioLoading = false"
          >
            <source :src="expertCase.audioUrl" type="audio/mpeg" />
          </audio>

          <div class="flex items-center gap-4 bg-slate-50 rounded-xl p-4 mb-6">
            <button
              @click="toggleAudio"
              :disabled="isAudioLoading"
              class="w-12 h-12 bg-amber-400 rounded-full flex items-center justify-center shadow-md hover:scale-105 transition-all flex-shrink-0 disabled:opacity-50"
            >
              <IconLoader v-if="isAudioLoading" class="w-5 h-5 text-white animate-spin" />
              <IconPause v-else-if="isPlayingAudio" class="w-5 h-5 text-white fill-current" />
              <IconPlay v-else class="w-5 h-5 text-white fill-current ml-0.5" />
            </button>

            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium text-slate-800 truncate">
                {{ expertCase.title }} - 音频解读
              </div>
              <div class="flex items-center gap-2 mt-2">
                <span class="text-xs text-slate-400 w-10 text-right">{{ formatTime(audioCurrentTime) }}</span>
                <div
                  class="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden cursor-pointer relative"
                  @click="seekAudio"
                >
                  <div
                    class="h-full bg-amber-400 transition-all duration-100"
                    :style="{ width: (audioDuration ? (audioCurrentTime / audioDuration) * 100 : 0) + '%' }"
                  />
                  <div
                    class="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-amber-400 rounded-full shadow-md cursor-grab active:cursor-grabbing"
                    :style="{ left: 'calc(' + (audioDuration ? (audioCurrentTime / audioDuration) * 100 : 0) + '% - 6px)' }"
                  />
                </div>
                <span class="text-xs text-slate-400 w-10">{{ formatTime(audioDuration) }}</span>
              </div>
            </div>
          </div>

          <div class="h-px bg-slate-100 mb-6"></div>
        </template>

        <!-- 正文内容 - Markdown 渲染 -->
        <div class="prose prose-slate max-w-none">
          <div v-if="expertCase.coverImage" class="mb-6 rounded-xl overflow-hidden">
            <img
              :src="expertCase.coverImage"
              alt="实战场景"
              class="w-full h-auto"
              referrerpolicy="no-referrer"
            />
          </div>

          <div class="text-slate-700 leading-relaxed" v-html="renderedContent"></div>
        </div>
      </div>

      <!-- 4. 讨论区 -->
      <div id="comment-section" class="bg-white rounded-lg shadow-sm p-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-base font-bold text-slate-900 flex items-center gap-2">
            <IconMessageSquare class="w-4 h-4 text-amber-400" />
            讨论区
            <span class="text-sm font-normal text-slate-400">({{ comments.length }})</span>
          </h3>
          <div class="flex gap-2">
            <button
              @click="commentFilter = 'all'"
              :class="commentFilter === 'all' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'"
              class="px-3 py-1 text-sm rounded-lg transition-colors"
            >
              全部
            </button>
            <button
              @click="commentFilter = 'featured'"
              :class="commentFilter === 'featured' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'"
              class="px-3 py-1 text-sm rounded-lg transition-colors"
            >
              精选
            </button>
          </div>
        </div>

        <!-- 评论输入 -->
        <div class="mb-6">
          <div class="flex gap-3">
            <div class="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-amber-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
              我
            </div>
            <div class="flex-1">
              <div class="bg-slate-50 rounded-xl p-4">
                <div v-if="replyTo" class="flex items-center justify-between mb-2 text-xs text-slate-500">
                  <span>正在回复...</span>
                  <button
                    @click="cancelReply"
                    class="text-slate-400 hover:text-slate-600"
                  >
                    <IconX class="w-3 h-3" />
                  </button>
                </div>
                <textarea
                  v-model="commentText"
                  @keydown="handleCommentKeydown"
                  placeholder="分享你的观点，与专家互动..."
                  class="w-full bg-transparent border-none outline-none resize-none text-slate-700 placeholder-slate-400 min-h-[120px]"
                  maxlength="5000"
                />
                <div class="flex items-center justify-between mt-3">
                  <div class="relative" ref="topicDropdownRef">
                    <button
                      @click="handleTopicClick"
                      class="flex items-center gap-1 text-slate-400 hover:text-slate-600 transition-colors text-sm"
                    >
                      <IconHash class="w-4 h-4" />
                      <span>话题</span>
                    </button>

                    <!-- 话题下拉 -->
                    <div v-if="showTopicDropdown" class="absolute top-full left-0 mt-2 w-40 bg-white rounded-xl shadow-lg border border-slate-100 py-2 z-50">
                      <button
                        v-for="topic in topics"
                        :key="topic"
                        @click="insertTopic(topic)"
                        class="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                      >
                        #{{ topic }}#
                      </button>
                    </div>
                  </div>
                  <div class="flex items-center gap-3">
                    <span class="text-xs text-slate-400">{{ commentText.length }}/5000</span>
                    <span class="text-xs text-slate-400">Ctrl+Enter 发送</span>
                    <button
                      @click="handleSubmitComment"
                      :disabled="!commentText.trim()"
                      class="px-4 py-2 bg-amber-400 text-white text-sm font-medium rounded-lg hover:bg-amber-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      <IconSend class="w-4 h-4" />
                      发布
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 评论列表 -->
        <div class="space-y-4">
          <div
            v-for="comment in filteredComments"
            :key="comment.id"
            class="flex gap-3 pb-4 border-b border-slate-100 last:border-none"
          >
            <div
              :class="comment.isOfficial
                ? 'bg-gradient-to-br from-blue-600 to-blue-700'
                : 'bg-gradient-to-br from-slate-400 to-slate-500'"
              class="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
            >
              {{ comment.avatar }}
            </div>

            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 mb-1">
                <span class="font-medium text-slate-900 text-sm">{{ comment.author }}</span>
                <span v-if="comment.isOfficial" class="px-1.5 py-0.5 bg-blue-600 text-white text-xs rounded">
                  官方
                </span>
                <span class="text-xs text-slate-400">{{ comment.date }}</span>
              </div>
              <p class="text-slate-700 leading-relaxed whitespace-pre-wrap text-sm">{{ comment.content }}</p>

              <!-- 回复列表 -->
              <div v-if="comment.replies && comment.replies.length > 0" class="mt-3 space-y-2">
                <div
                  v-for="reply in comment.replies"
                  :key="reply.id"
                  class="flex gap-2 bg-slate-50 rounded-lg p-3"
                >
                  <div class="w-7 h-7 rounded-full bg-gradient-to-br from-slate-300 to-slate-400 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                    {{ reply.avatar }}
                  </div>
                  <div class="flex-1">
                    <div class="flex items-center gap-2 mb-1">
                      <span class="font-medium text-slate-900 text-xs">{{ reply.author }}</span>
                      <span class="text-xs text-slate-400">{{ reply.date }}</span>
                    </div>
                    <p class="text-slate-700 text-xs leading-relaxed">{{ reply.content }}</p>
                  </div>
                </div>
              </div>

              <div class="flex items-center gap-4 mt-2">
                <button
                  @click="handleReplyClick(comment.id)"
                  :class="replyTo === comment.id ? 'text-amber-500' : 'text-slate-400 hover:text-slate-600'"
                  class="flex items-center gap-1 transition-colors text-xs"
                >
                  <IconMessageSquare class="w-3.5 h-3.5" />
                  <span>回复</span>
                </button>
                <button
                  @click="handleCommentLike(comment.id)"
                  :class="commentLikes.has(comment.id) ? 'text-amber-500' : 'text-slate-400 hover:text-slate-600'"
                  class="flex items-center gap-1 transition-colors text-xs"
                >
                  <IconThumbsUp :class="commentLikes.has(comment.id) ? 'fill-current' : ''" class="w-3.5 h-3.5" />
                  <span>{{ (comment.likes || 0) + (commentLikes.has(comment.id) ? 1 : 0) }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast 提示 -->
    <transition name="toast">
      <div
        v-if="toast"
        class="fixed bottom-32 left-1/2 -translate-x-1/2 z-[100] bg-slate-900 text-white px-6 py-3 rounded-2xl shadow-2xl flex items-center gap-3"
      >
        <div v-if="toast.type === 'success'" class="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
          <IconCheck class="w-3 h-3 text-white" />
        </div>
        <div v-else class="w-5 h-5 rounded-full bg-amber-400 flex items-center justify-center">
          <span class="text-white text-xs font-bold">!</span>
        </div>
        <span class="text-sm font-medium">{{ toast.message }}</span>
      </div>
    </transition>

    <!-- 微信分享二维码模态框 -->
    <transition name="modal">
      <div
        v-if="showWechatModal"
        class="fixed inset-0 z-[100] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
        @click="showWechatModal = false"
      >
        <div
          class="bg-white rounded-3xl p-8 max-w-sm w-full text-center"
          @click.stop
        >
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-lg font-bold text-slate-800 flex items-center gap-2">
              <IconMessageCircle class="w-5 h-5 text-green-500" />
              微信扫码分享
            </h3>
            <button
              @click="showWechatModal = false"
              class="p-2 hover:bg-slate-100 rounded-full transition-colors"
            >
              <IconX class="w-5 h-5 text-slate-400" />
            </button>
          </div>
          <div class="bg-slate-50 rounded-2xl p-8 mb-6">
            <div class="w-48 h-48 mx-auto bg-white rounded-xl p-4 shadow-sm">
              <img
                :src="`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(window.location.href)}`"
                alt="微信分享二维码"
                class="w-full h-full"
              />
            </div>
          </div>
          <p class="text-sm text-slate-500">
            打开微信扫一扫，快速分享给战友
          </p>
        </div>
      </div>
    </transition>

    <!-- 团队分享模态框 -->
    <transition name="modal">
      <div
        v-if="showTeamShareModal"
        class="fixed inset-0 z-[100] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
        @click="showTeamShareModal = false"
      >
        <div
          class="bg-white rounded-3xl p-6 max-w-md w-full"
          @click.stop
        >
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-lg font-bold text-slate-800 flex items-center gap-2">
              <IconUsers class="w-5 h-5 text-amber-500" />
              分享给战友
            </h3>
            <button
              @click="showTeamShareModal = false"
              class="p-2 hover:bg-slate-100 rounded-full transition-colors"
            >
              <IconX class="w-5 h-5 text-slate-400" />
            </button>
          </div>

          <div class="space-y-3 max-h-80 overflow-y-auto">
            <button
              v-for="(colleague, idx) in colleagues"
              :key="idx"
              class="w-full flex items-center gap-4 p-3 rounded-xl hover:bg-slate-50 transition-colors border border-transparent hover:border-slate-100"
              @click="handleShareToColleague(colleague)"
            >
              <div class="w-12 h-12 rounded-full bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center text-white font-bold text-sm">
                {{ colleague.avatar }}
              </div>
              <div class="text-left">
                <div class="font-bold text-slate-800">{{ colleague.name }}</div>
                <div class="text-xs text-slate-500">{{ colleague.role }}</div>
              </div>
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- 右下角悬浮操作按钮组 -->
    <div class="fixed bottom-6 right-6 z-50 flex flex-col gap-3">
      <button
        @click="handleLikeToggle"
        :class="isLiked
          ? 'bg-amber-400 text-white'
          : 'bg-white text-slate-400 hover:text-amber-400'"
        class="w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-110"
        :title="isLiked ? '已点赞' : '点赞'"
      >
        <IconThumbsUp :class="isLiked ? 'fill-current' : ''" class="w-5 h-5" />
      </button>

      <button
        @click="scrollToComment"
        class="w-12 h-12 rounded-full bg-white shadow-lg text-slate-400 hover:text-amber-400 flex items-center justify-center transition-all duration-200 hover:scale-110"
        title="写评论"
      >
        <IconMessageSquare class="w-5 h-5" />
      </button>

      <div class="relative" ref="folderDropdownRef">
        <button
          @click="handleBookmarkToggle"
          :class="isBookmarked
            ? 'bg-amber-400 text-white'
            : 'bg-white text-slate-400 hover:text-amber-400'"
          class="w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-110"
          :title="isBookmarked ? '已收藏' : '收藏'"
        >
          <IconStar :class="isBookmarked ? 'fill-current' : ''" class="w-5 h-5" />
        </button>

        <transition name="dropdown">
          <div
            v-if="showFolderDropdown"
            class="absolute bottom-full right-0 mb-2 w-48 bg-white rounded-xl shadow-lg border border-slate-100 py-2 z-50 overflow-hidden"
          >
            <div class="px-4 py-2 text-xs text-slate-400 uppercase tracking-wider border-b border-slate-50">
              选择收藏夹
            </div>
            <button
              v-for="folder in folders"
              :key="folder"
              @click="handleFolderSelect(folder)"
              :class="selectedFolder === folder
                ? 'bg-amber-100 text-amber-600'
                : 'text-slate-700 hover:bg-slate-50'"
              class="w-full px-4 py-2.5 text-left text-sm transition-colors flex items-center justify-between"
            >
              <span class="flex items-center gap-2">
                <IconFolderPlus v-if="folder === '新建文件夹...'" class="w-4 h-4" />
                <IconStar v-else :class="selectedFolder === folder ? 'fill-current' : ''" class="w-4 h-4" />
                {{ folder }}
              </span>
              <IconCheck v-if="selectedFolder === folder && folder !== '新建文件夹...'" class="w-4 h-4" />
            </button>
          </div>
        </transition>
      </div>

      <div class="relative" ref="shareDropdownRef">
        <button
          @click="handleShareClick"
          :class="showShareDropdown
            ? 'bg-amber-400 text-white'
            : 'bg-white text-slate-400 hover:text-amber-400'"
          class="w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-110"
          title="分享"
        >
          <IconShare2 class="w-5 h-5" />
        </button>

        <transition name="dropdown">
          <div
            v-if="showShareDropdown"
            class="absolute bottom-full right-0 mb-2 w-44 bg-white rounded-xl shadow-lg border border-slate-100 py-2 z-50 overflow-hidden"
          >
            <button
              @click="handleCopyLink"
              class="w-full px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-3"
            >
              <IconLink2 class="w-4 h-4 text-slate-400" />
              复制链接
            </button>
            <button
              @click="handleWechatShare"
              class="w-full px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-3"
            >
              <IconMessageCircle class="w-4 h-4 text-green-500" />
              分享到微信
            </button>
            <button
              @click="handleTeamShare"
              class="w-full px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-3"
            >
              <IconUsers class="w-4 h-4 text-amber-500" />
              内部团队分享
            </button>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import { EXPERTS } from '../../data'
import { sanitizeHtml } from '../../utils/sanitize'
import {
  IconPlay,
  IconPause,
  IconMaximize2,
  IconCheck,
  IconUsers,
  IconX,
  IconMessageCircle,
  IconLink2,
  IconFolderPlus,
  IconThumbsUp,
  IconStar,
  IconSend,
  IconHash,
  IconMessageSquare,
  IconShare2,
  IconClock,
  IconEye,
  IconLoader,
  IconMinimize2
} from '../icons'

export default {
  name: 'ExpertCaseDetail',
  components: {
    IconPlay,
    IconPause,
    IconMaximize2,
    IconCheck,
    IconUsers,
    IconX,
    IconMessageCircle,
    IconLink2,
    IconFolderPlus,
    IconThumbsUp,
    IconStar,
    IconSend,
    IconHash,
    IconMessageSquare,
    IconShare2,
    IconClock,
    IconEye,
    IconLoader,
    IconMinimize2
  },
  props: {
    expertCase: {
      type: Object,
      required: true
    },
    onClose: {
      type: Function,
      default: null
    },
    autoFocusMedia: {
      type: String,
      default: null
    },
    onTrackInteraction: {
      type: Function,
      default: null
    },
    initialIsBookmarked: {
      type: Boolean,
      default: false
    },
    initialIsLiked: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isLiked: this.initialIsLiked,
      isBookmarked: this.initialIsBookmarked,
      showShareDropdown: false,
      showWechatModal: false,
      showTeamShareModal: false,
      showFolderDropdown: false,
      selectedFolder: '未分类',
      toast: null,
      commentText: '',
      replyTo: null,
      commentLikes: new Set(),
      comments: [
        {
          id: '1',
          author: '魏红亮',
          avatar: '魏',
          date: '2023-05-24',
          content: '宁老师的这节课，我对着电脑把四象限的图画下来，然后看着文字看了两三遍，最后用一个大拇哥来表达敬意！\n\n胡光书老师的《精益管理实战课》中就提到八大浪费，其中有一个是"员工积极性的浪费"...',
          isOfficial: true,
          likes: 12,
          replies: []
        }
      ],
      commentFilter: 'all',
      isPlayingVideo: false,
      videoSpeed: 1,
      videoDuration: 0,
      videoCurrentTime: 0,
      isVideoLoading: true,
      isFullscreen: false,
      isPlayingAudio: false,
      audioDuration: 0,
      audioCurrentTime: 0,
      isAudioLoading: true,
      showTopicDropdown: false,
      topics: ['执行力', '团队管理', '绩效面谈', '跨部门协作', '人才保留'],
      folders: ['未分类', '管理干货', '团队建设', '新建文件夹...'],
      colleagues: [
        { name: '王志强', role: '产品总监', avatar: 'WZ' },
        { name: '李晓红', role: 'HRBP', avatar: 'LX' },
        { name: '陈建华', role: '技术负责人', avatar: 'CJ' },
        { name: '赵敏', role: '运营经理', avatar: 'ZM' },
        { name: '刘建国', role: '销售总监', avatar: 'LJ' }
      ]
    }
  },
  computed: {
    expert() {
      return EXPERTS.find(e => e.id === this.expertCase.expertId)
    },
    autoFocus() {
      return this.autoFocusMedia || this.$route.query.type
    },
    filteredComments() {
      if (this.commentFilter === 'featured') {
        return this.comments.filter(c => c.isOfficial)
      }
      return this.comments
    },
    renderedContent() {
      // Simple markdown to HTML conversion for basic formatting
      // In a real app, use marked library as specified in the project docs
      if (!this.expertCase.content) return ''
      let html = this.expertCase.content
        .replace(/^### (.*$)/gim, '<h3 class="text-lg font-bold text-slate-900 mt-6 mb-3">$1</h3>')
        .replace(/^## (.*$)/gim, '<h2 class="text-xl font-bold text-slate-900 mt-6 mb-3">$1</h2>')
        .replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold text-slate-900 mt-6 mb-3">$1</h1>')
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/`([^`]+)`/g, '<code class="bg-slate-100 px-1 py-0.5 rounded text-sm">$1</code>')
        .replace(/^\d+\.\s+(.*$)/gim, '<li class="ml-4">$1</li>')
        .replace(/\n/g, '<br>')
      // XSS 消毒：所有 v-html 内容必须经过 DOMPurify
      return sanitizeHtml(html)
    }
  },
  mounted() {
    this.bindClickOutside()
    this.bindFullscreenChange()
    this.autoScroll()
  },
  beforeDestroy() {
    this.unbindClickOutside()
    this.unbindFullscreenChange()
  },
  methods: {
    showToast(message, type = 'info') {
      this.toast = { message, type }
      setTimeout(() => {
        this.toast = null
      }, 2000)
    },
    // 分享功能
    handleShareClick() {
      this.showShareDropdown = !this.showShareDropdown
      this.showFolderDropdown = false
    },
    async handleCopyLink() {
      try {
        await navigator.clipboard.writeText(window.location.href)
        this.showToast('链接已复制', 'success')
      } catch {
        this.showToast('复制失败', 'info')
      }
      this.showShareDropdown = false
    },
    handleWechatShare() {
      this.showShareDropdown = false
      this.showWechatModal = true
    },
    handleTeamShare() {
      this.showShareDropdown = false
      this.showTeamShareModal = true
    },
    handleShareToColleague(colleague) {
      this.showToast(`已分享给 ${colleague.name}`, 'success')
      this.showTeamShareModal = false
    },
    // 点赞功能
    handleLikeToggle() {
      this.isLiked = !this.isLiked
      this.showToast(this.isLiked ? '已点赞！' : '已取消点赞', this.isLiked ? 'success' : 'info')
      if (this.onTrackInteraction) {
        this.onTrackInteraction('like')
      }
    },
    // 评论点赞
    handleCommentLike(commentId) {
      const newSet = new Set(this.commentLikes)
      if (newSet.has(commentId)) {
        newSet.delete(commentId)
        this.showToast('已取消点赞', 'info')
      } else {
        newSet.add(commentId)
        this.showToast('点赞成功！', 'success')
      }
      this.commentLikes = newSet
    },
    // 回复评论
    handleReplyClick(commentId) {
      if (this.replyTo === commentId) {
        this.replyTo = null
        this.commentText = ''
      } else {
        this.replyTo = commentId
        const comment = this.comments.find(c => c.id === commentId)
        if (comment) {
          this.commentText = `@${comment.author} `
        }
      }
    },
    cancelReply() {
      this.replyTo = null
      this.commentText = ''
    },
    // 插入话题
    handleTopicClick() {
      this.showTopicDropdown = !this.showTopicDropdown
    },
    insertTopic(topic) {
      this.commentText = this.commentText + `#${topic}# `
      this.showTopicDropdown = false
    },
    // 收藏功能
    handleBookmarkToggle() {
      if (!this.isBookmarked) {
        this.isBookmarked = true
        this.showToast('已收藏！', 'success')
      } else {
        this.isBookmarked = false
        this.showToast('已取消收藏', 'info')
      }
      if (this.onTrackInteraction) {
        this.onTrackInteraction('bookmark')
      }
    },
    // 提交评论
    handleSubmitComment() {
      if (!this.commentText.trim()) return

      const newComment = {
        id: Date.now().toString(),
        author: '当前用户',
        avatar: '我',
        date: new Date().toISOString().split('T')[0],
        content: this.commentText.trim(),
        isOfficial: false,
        likes: 0,
        replies: []
      }

      if (this.replyTo) {
        // 添加回复
        this.comments = this.comments.map(c => {
          if (c.id === this.replyTo) {
            return {
              ...c,
              replies: [...(c.replies || []), {
                id: Date.now().toString(),
                author: '当前用户',
                avatar: '我',
                date: new Date().toISOString().split('T')[0],
                content: this.commentText.trim()
              }]
            }
          }
          return c
        })
        this.showToast('回复发布成功！', 'success')
        this.replyTo = null
      } else {
        this.comments = [newComment, ...this.comments]
        this.showToast('评论发布成功！', 'success')
      }

      this.commentText = ''

      if (this.onTrackInteraction) {
        this.onTrackInteraction('comment')
      }
    },
    handleCommentKeydown(e) {
      if (e.ctrlKey && e.key === 'Enter') {
        this.handleSubmitComment()
      }
    },
    handleFolderSelect(folder) {
      if (folder === '新建文件夹...') {
        this.showToast('创建新文件夹功能即将上线', 'info')
        this.showFolderDropdown = false
        return
      }
      this.selectedFolder = folder
      this.showFolderDropdown = false
      this.showToast(`已保存到「${folder}」`, 'success')
    },
    // 点击外部关闭下拉菜单
    bindClickOutside() {
      this._clickOutsideHandler = (event) => {
        if (this.$refs.shareDropdownRef && !this.$refs.shareDropdownRef.contains(event.target)) {
          this.showShareDropdown = false
        }
        if (this.$refs.folderDropdownRef && !this.$refs.folderDropdownRef.contains(event.target)) {
          this.showFolderDropdown = false
        }
        if (this.$refs.topicDropdownRef && !this.$refs.topicDropdownRef.contains(event.target)) {
          this.showTopicDropdown = false
        }
      }
      document.addEventListener('mousedown', this._clickOutsideHandler)
    },
    unbindClickOutside() {
      document.removeEventListener('mousedown', this._clickOutsideHandler)
    },
    // 自动滚动
    autoScroll() {
      if (!this.autoFocus) return
      setTimeout(() => {
        let targetRef = null
        if (this.autoFocus === 'video') targetRef = this.$refs.videoSectionRef
        else if (this.autoFocus === 'text') targetRef = this.$refs.contentSectionRef

        if (targetRef) {
          const top = targetRef.offsetTop - 100
          window.scrollTo({ top, behavior: 'smooth' })
        }
      }, 100)
    },
    // 视频控制
    toggleVideo() {
      const video = this.$refs.videoRef
      if (!video) return
      if (this.isPlayingVideo) {
        video.pause()
      } else {
        video.play()
      }
      this.isPlayingVideo = !this.isPlayingVideo
    },
    onVideoTimeUpdate() {
      this.videoCurrentTime = this.$refs.videoRef ? this.$refs.videoRef.currentTime : 0
    },
    onVideoLoadedMetadata() {
      this.videoDuration = this.$refs.videoRef ? this.$refs.videoRef.duration : 0
      this.isVideoLoading = false
    },
    handleVideoSpeed() {
      const speeds = [0.5, 1, 1.5, 2]
      const nextIndex = (speeds.indexOf(this.videoSpeed) + 1) % speeds.length
      const nextSpeed = speeds[nextIndex]
      this.videoSpeed = nextSpeed
      if (this.$refs.videoRef) this.$refs.videoRef.playbackRate = nextSpeed
    },
    // 全屏功能
    async toggleFullscreen() {
      const container = this.$refs.videoContainerRef
      if (!container) return
      try {
        if (!document.fullscreenElement) {
          await container.requestFullscreen()
          this.isFullscreen = true
        } else {
          await document.exitFullscreen()
          this.isFullscreen = false
        }
      } catch (error) {
        console.error('全屏切换失败:', error)
        this.showToast('全屏切换失败', 'info')
      }
    },
    bindFullscreenChange() {
      this._fullscreenHandler = () => {
        this.isFullscreen = !!document.fullscreenElement
      }
      document.addEventListener('fullscreenchange', this._fullscreenHandler)
    },
    unbindFullscreenChange() {
      document.removeEventListener('fullscreenchange', this._fullscreenHandler)
    },
    // 音频控制
    toggleAudio() {
      const audio = this.$refs.audioRef
      if (!audio) return
      if (this.isPlayingAudio) {
        audio.pause()
      } else {
        audio.play()
      }
      this.isPlayingAudio = !this.isPlayingAudio
    },
    onAudioTimeUpdate() {
      this.audioCurrentTime = this.$refs.audioRef ? this.$refs.audioRef.currentTime : 0
    },
    onAudioLoadedMetadata() {
      this.audioDuration = this.$refs.audioRef ? this.$refs.audioRef.duration : 0
      this.isAudioLoading = false
    },
    seekAudio(e) {
      const rect = e.currentTarget.getBoundingClientRect()
      const percent = (e.clientX - rect.left) / rect.width
      if (this.$refs.audioRef) {
        this.$refs.audioRef.currentTime = percent * this.audioDuration
      }
    },
    formatTime(time) {
      const minutes = Math.floor(time / 60)
      const seconds = Math.floor(time % 60)
      return `${minutes}:${seconds.toString().padStart(2, '0')}`
    },
    scrollToComment() {
      const commentSection = document.getElementById('comment-section')
      if (commentSection) {
        commentSection.scrollIntoView({ behavior: 'smooth' })
      }
    },
    // 处理视频点击播放
    handleVideoContainerClick(e) {
      // 如果点击的是控制按钮区域，不触发播放
      if (e.target.closest('.video-controls')) return
      this.toggleVideo()
    }
  }
}
</script>

<style scoped>
/* Toast 动画 */
.toast-enter-active, .toast-leave-active {
  transition: all 0.2s ease;
}
.toast-enter, .toast-leave-to {
  opacity: 0;
  transform: translate(-50%, 50px) scale(0.9);
}

/* 模态框动画 */
.modal-enter-active, .modal-leave-active {
  transition: opacity 0.2s ease;
}
.modal-enter, .modal-leave-to {
  opacity: 0;
}
.modal-enter-active > div, .modal-leave-active > div {
  transition: transform 0.2s ease, opacity 0.2s ease;
}
.modal-enter > div, .modal-leave-to > div {
  transform: scale(0.9);
  opacity: 0;
}

/* 下拉菜单动画 */
.dropdown-enter-active, .dropdown-leave-active {
  transition: all 0.15s ease;
}
.dropdown-enter, .dropdown-leave-to {
  opacity: 0;
  transform: translateY(10px) scale(0.95);
}

/* 标题区入场动画 */
@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 内容区入场动画 */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
